import {
  WSActions,
  WSElement,
  WSFlexBox,
  WSForm,
  WSGrid,
  WSInfoBox,
  WSInputText,
  WSInputTextarea,
  WSLabel,
  WSList,
  WSPanel,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import * as Yup from "yup";
import { ObjectSchema } from "yup";

import { DataItem } from "../../../../../../shared/components/DataItem";
import { AlertBillableRequirement } from "../../../../components/AlertBillableRequirement";
import {
  getExpirationStrategyDefaultValues,
  getReviewStrategyDefaultValues
} from "../../../../components/CreateRequirementDefinitionForm/utils";
import { requirementDefinitionDefaultValidationSchema } from "../../../../components/CreateRequirementDefinitionForm/validationSchema";
import { FormSectionApprovals } from "../../../../components/FormSectionApprovals";
import { FormSectionExpiration } from "../../../../components/FormSectionExpiration";
import { RequirementDefinitionFormValues } from "../../../../types";
import { RequirementDefinitionTemplate } from "../../types";

type CreateRequirementDefinitionFormProps = {
  formData?: RequirementDefinitionFormValues;
  template: RequirementDefinitionTemplate;
  onBack: () => void;
  onSuccess: (formData: RequirementDefinitionFormValues) => void;
};

const getDefaultValues = (
  template: RequirementDefinitionTemplate,
  formData?: RequirementDefinitionFormValues
): Partial<RequirementDefinitionFormValues> =>
  formData
    ? formData
    : {
        contextType: Array.isArray(template.contextType)
          ? template.contextType[0]
          : template.contextType,
        description: template.modal.description || "",
        name: template.isCustomName ? "" : template.name,
        type: template.requirementType,
        ...getReviewStrategyDefaultValues(
          template.isRequirementWithErrorStates
        ),
        ...getExpirationStrategyDefaultValues(!template.noExpirationForm),
        ...template.form?.getDefaultSettingsValues(template)
      };

const HiddenFields: React.FC<{ template: RequirementDefinitionTemplate }> = ({
  template
}) => {
  const { register } = useFormContext();

  useEffect(() => {
    const defaultFields = template.form?.getDefaultSettingsValues(template);

    if (!template.form?.partialForm && defaultFields) {
      Object.keys(defaultFields).forEach(key => {
        register(key);
      });
    }
  }, [template, register]);

  return null;
};

export const CreateRequirementDefinitionByTemplateForm: React.FC<
  CreateRequirementDefinitionFormProps
> = ({ onBack, onSuccess, template, formData }) => {
  const defaultValues = useMemo(
    () => getDefaultValues(template, formData),
    [template, formData]
  );

  const partialForm: React.ReactNode = useMemo(() => {
    return template.form?.partialForm || null;
  }, [template.form]);

  const validationSchema = useMemo(() => {
    const validations = template.form?.validationSchema
      ? template.form?.validationSchema
      : Yup.object().shape({});

    return requirementDefinitionDefaultValidationSchema({
      noExpirationForm: !!template.noExpirationForm
    }).concat(validations as ObjectSchema<any>);
  }, [template]);

  return (
    <WSForm<RequirementDefinitionFormValues>
      defaultValues={defaultValues}
      validationSchema={validationSchema}
      onSubmit={async formValues => {
        onSuccess(formValues);
      }}
    >
      <HiddenFields template={template} />
      <WSList gap="XL">
        <WSPanel>
          <WSList gap="XL">
            <WSText.Paragraph mb="S" weight="medium">
              Requirement information
            </WSText.Paragraph>
            <WSGrid gutter={{ m: "2XL" }}>
              <WSGrid.Item span={{ m: "6" }}>
                {template.isCustomName ? (
                  <WSForm.Field
                    component={WSInputText}
                    componentProps={{
                      required: true
                    }}
                    label="Name"
                    name="name"
                    placeholder="Requirement name"
                  />
                ) : (
                  <>
                    <DataItem label="Name" value={template.name} />
                    <WSForm.Field component={WSInputText} hidden name="name" />
                  </>
                )}
              </WSGrid.Item>

              <WSGrid.Item span={{ m: "6" }}>
                <DataItem
                  icon={template.icon}
                  label="Type"
                  secondaryText={template.helperText}
                  value={template.name}
                />
                <WSForm.Field component={WSInputText} hidden name="type" />
              </WSGrid.Item>
            </WSGrid>
            <AlertBillableRequirement
              requirementName={template.name}
              template={template}
            />
            {template.payerInfo ? (
              <WSInfoBox theme="info">
                <WSList.Horizontal gap="S">
                  <WSText.ParagraphSm>Description</WSText.ParagraphSm>
                  <WSPill icon="lock" text="Private" theme="neutral" />
                </WSList.Horizontal>
                <WSFlexBox direction="column" gap="L">
                  <WSElement>
                    <WSLabel text="Description" />
                    <WSText.ParagraphSm color="gray600" weight="book">
                      {template.payerInfo.description}
                    </WSText.ParagraphSm>
                  </WSElement>

                  {template.payerInfo.exampleUseCases && (
                    <WSElement>
                      <WSLabel text="Example use cases" />
                      <WSText.ParagraphSm color="gray600" weight="book">
                        {template.payerInfo.exampleUseCases}
                      </WSText.ParagraphSm>
                    </WSElement>
                  )}
                  {template.payerInfo.disclaimer && (
                    <WSElement>
                      <WSLabel text="Disclaimer" />
                      <WSText.ParagraphSm color="gray600" weight="book">
                        {template.payerInfo.disclaimer}
                      </WSText.ParagraphSm>
                    </WSElement>
                  )}
                  <WSElement>
                    <WSLabel text="Cost" />
                    <WSText.ParagraphSm color="gray600" weight="book">
                      {template.payerInfo.cost}
                    </WSText.ParagraphSm>
                  </WSElement>
                </WSFlexBox>
                <WSForm.Field
                  component={WSInputText}
                  hidden
                  name="description"
                />
              </WSInfoBox>
            ) : (
              <WSForm.Field
                component={WSInputTextarea}
                componentProps={{
                  helperText:
                    "The description is visible internally to you and your team only"
                }}
                hidden={template.payerInfo !== null}
                label="Description"
                name="description"
                placeholder="Requirement description"
              />
            )}
          </WSList>
        </WSPanel>

        {partialForm}

        <WSForm.Field component={WSInputText} hidden name="contextType" />

        <FormSectionExpiration noExpirationForm={!!template.noExpirationForm} />

        <FormSectionApprovals
          isRequirementWithErrorStates={template.isRequirementWithErrorStates}
        />

        <WSForm.Debug />

        <WSForm.Data<RequirementDefinitionFormValues>>
          {formData => (
            <WSActions
              alignment="fill"
              buttons={[
                {
                  kind: "Tertiary",
                  label: "Cancel",
                  onClick: onBack
                },
                {
                  disabled: !validationSchema.isValidSync(formData),
                  kind: "Primary",
                  label: "Continue",
                  type: "submit"
                }
              ]}
              orientation="horizontal"
            />
          )}
        </WSForm.Data>
      </WSList>
    </WSForm>
  );
};
